import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Spinner } from "./Spinner";

// Height / horizontal padding / font-size — matches §8b exactly.
const SIZE = {
  sm: "h-8 px-[14px] text-[14px]",
  md: "h-10 px-[18px] text-[15px]",
  lg: "h-12 px-[24px] text-[16px]",
};

const VARIANT = {
  primary:
    "bg-frac-purple text-frac-white hover:bg-frac-purple-90 active:bg-frac-black",
  secondary:
    "bg-transparent text-frac-purple border border-frac-purple hover:bg-frac-purple-10 active:bg-frac-purple-30",
  ghost:
    "bg-transparent text-frac-purple hover:bg-frac-purple-10 active:bg-frac-purple-30",
  dark: "bg-frac-black text-frac-white hover:bg-surface-raised",
  "primary-on-dark":
    "bg-frac-violet text-frac-white hover:bg-[#8A1AE8] hover:shadow-glow",
  "secondary-on-dark":
    "bg-transparent text-frac-white border border-hairline hover:border-active-border hover:bg-[rgba(114,0,219,0.08)]",
};

/**
 * Primary button primitive.
 *
 * Renders a <button> by default. Two ways to render as something else:
 *   - String API:      as="link" + to="/path"    (React Router)
 *                      as="a"    + href="…"      (native anchor)
 *   - Component API:   as={Link} to="/path"      (any React component)
 *                      as={ExtLink} href="…"
 * Both are supported so usage sites can pick either idiom without breaking.
 */
export const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled = false,
    as = "button",
    to,
    href,
    type = "button",
    className,
    children,
    ...rest
  },
  ref,
) {
  const classes = cn(
    "relative inline-flex items-center justify-center gap-2 rounded-[10px] font-semibold whitespace-nowrap transition-all duration-fast ease-out",
    "disabled:opacity-45 disabled:cursor-not-allowed",
    SIZE[size],
    VARIANT[variant],
    className,
  );

  const content = (
    <>
      <span
        className={cn(
          "inline-flex items-center gap-2",
          loading && "invisible",
        )}
      >
        {children}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size={size === "lg" ? "md" : "sm"} />
        </span>
      )}
    </>
  );

  if (as === "link" && to) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (as === "a" && href) {
    return (
      <a ref={ref} href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  // Component-as-prop (e.g., as={Link} to="/x", as={NavLink} to="/y")
  if (typeof as !== "string") {
    const Component = as;
    return (
      <Component
        ref={ref}
        {...(to !== undefined && { to })}
        {...(href !== undefined && { href })}
        className={classes}
        {...rest}
      >
        {content}
      </Component>
    );
  }
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
